import type { Customer, DocumentLanguage, InvoiceCurrency, LineItem } from "./types.ts";

export interface DocumentDefaults {
  documentLanguage: DocumentLanguage;
  currency: InvoiceCurrency;
}

/**
 * FIN-1580: the invoice document's default language and currency, resolved from the customer.
 * Swedish for a Swedish customer, English otherwise (specs/invoice-document-i18n/requirements.md,
 * AC2) — always overridable per invoice afterwards. Currency always defaults to SEK; there is no
 * per-customer/per-company default for it (scope confirmed in requirements.md — a fixed 4-value
 * list, no currency inference).
 *
 * Called authoritatively server-side inside `draftReceivable()` (so `apps/mcp`'s `invoices.draft`,
 * which renders no UI, gets the identical default with zero MCP-side code) and client-side only
 * for instant UI pre-fill once a customer is picked — the same "server enforces, client previews"
 * split already used for FIN-603/FIN-283.
 */
export function resolveDocumentDefaults(customer: Pick<Customer, "country">): DocumentDefaults {
  return {
    documentLanguage: customer.country === "SE" ? "sv" : "en",
    currency: "SEK",
  };
}

/**
 * The draft patch both apps apply when a customer is picked or created on a form: re-derive
 * VAT for the new customer, and resolve document-default language/currency only on the draft's
 * FIRST customer pick (`isFirstPick`) — a later customer change on the same draft never
 * overwrites a choice the sender may have already made. Extracted so `src/pages/CreateInvoice.tsx`
 * and `apps/client`'s InvoiceGeneratorClient (two separate apps, no shared component layer) don't
 * each hand-roll the identical four-line patch. Takes `applyVat` as a parameter rather than
 * importing it from `./vat.ts` directly: that file imports `@financialharmony/tax-se` as a bare
 * specifier, which only resolves under tsc/vite's workspace linking, not Deno's import
 * resolution — this file is also reachable from the Deno backend (via draftReceivable()), and
 * importing vat.ts from here would pull that Deno-incompatible import into its type-check graph.
 */
export function applyCustomerSelection(
  draft: { lines: LineItem[]; documentLanguage: DocumentLanguage },
  customer: Customer | null,
  isFirstPick: boolean,
  applyVat: (customer: Customer | null, lines: LineItem[], language: DocumentLanguage) => LineItem[],
): { lines: LineItem[] } & Partial<DocumentDefaults> {
  return {
    lines: applyVat(customer, draft.lines, draft.documentLanguage),
    ...(isFirstPick && customer ? resolveDocumentDefaults(customer) : {}),
  };
}
